import React, { useState } from 'react';
import PageTitle from '../headers/PageTitle';
import { FaPlus } from 'react-icons/fa';
import { BsMicrosoftTeams } from 'react-icons/bs';
import MobileWarningDialog from '../DesignSystems/MobileWarning/MobileWarningDialog';
import FullCalendar from '@fullcalendar/react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import timegrid from '@fullcalendar/timegrid'; // a plugin!
import interactionPlugin from '@fullcalendar/interaction'; // a plugin!
import byPeriodPlugin from './CalendarByOperationalPeriodPlugin'; // custom plugin
import { useIncidentCalendarEvents } from './hooks/incidentCalendarHooks';
import CalendarEventEditDrawer from './CalendarEventEditDrawer';
import CalendarEventAddDrawer from './CalendarEventAddDrawer';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';

const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

const sanitizeTeamsJoinUrl = (url?: string | null): string | null => {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);

    if (parsed.protocol !== 'https:') {
      return null;
    }

    const hostname = parsed.hostname.toLowerCase();

    // Allow primary and common Microsoft Teams hostnames/subdomains.
    const allowedHosts = [
      'teams.microsoft.com',
      'teams.microsoft.us',
      'gov.teams.microsoft.us',
    ];

    const isAllowedHost =
      allowedHosts.includes(hostname) ||
      allowedHosts.some((allowed) => hostname.endsWith(`.${allowed}`));

    if (!isAllowedHost) {
      return null;
    }

    return parsed.toString();
  } catch {
    return null;
  }
};

const IAPCalendar: React.FC = () => {
  const { data: calendarEvents } = useIncidentCalendarEvents();
  const { exportAsForm } = useIcsFormPdfPreview();

  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<any>(null);

  const handleAddEvent = () => {
    setShowAddDrawer(true);
  };

  const handleExportICS230 = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({ formname: 'ICS_230_CG', mapSketch: null, format });
  };
  const editExistingEvent = (event: any) => {
    const selectedEvent = calendarEvents?.find((e) => e.id === event.id);

    if (!selectedEvent) {
      console.error('Selected event not found in calendar events');
      return;
    }
    setSelectedCalendarEvent(selectedEvent);
    setShowEditDrawer(true);
  };

  return (
    <MobileWarningDialog>
      <div className="maincontent">
        <PageTitle title="Calendar">
          <StylishNewButton
            className="button--primary"
            onClick={handleAddEvent}
          >
            <FaPlus className="me-2" />
            Add Event
          </StylishNewButton>
          <span className="ms-2">
            <FormExportButton
              formname="ICS_230_CG"
              onExport={handleExportICS230}
              label="Preview ICS 230"
            />
          </span>
        </PageTitle>
        <div className="calendar">
          <FullCalendar
            validRange={{ start: firstDay.toISOString() }}
            plugins={[
              dayGridPlugin,
              timegrid,
              byPeriodPlugin,
              interactionPlugin,
            ]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'timeGridDay,timeGridWeek,dayGridMonth,byPeriod',
            }}
            initialView="byPeriod"
            events={calendarEvents?.map((event) => ({
              ...event,
              eventClickCallback: () => editExistingEvent({ id: event.id }), // Custom plugins do not have access to eventClick
            }))}
            eventContent={(arg) => {
              const rawTeamsJoinUrl = arg.event.extendedProps.teamsJoinUrl as
                | string
                | null
                | undefined;
              const teamsJoinUrl = sanitizeTeamsJoinUrl(rawTeamsJoinUrl);
              return (
                <div className="d-flex align-items-center gap-1 w-100 overflow-hidden">
                  <div className="fc-event-time">{arg.timeText}</div>
                  <div className="fc-event-title text-truncate">
                    {arg.event.title}
                  </div>
                  {teamsJoinUrl && (
                    <a
                      href={teamsJoinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Join Teams Meeting"
                      className="ms-auto flex-shrink-0 d-inline-flex align-items-center gap-1 px-1 rounded"
                      style={{
                        backgroundColor: '#6264A7',
                        color: '#fff',
                        fontSize: '11px',
                        lineHeight: 1,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#464775')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = '#6264A7')
                      }
                    >
                      <BsMicrosoftTeams size={12} />
                      Join
                    </a>
                  )}
                </div>
              );
            }}
            eventClick={(info) => editExistingEvent(info.event)}
          />
        </div>
        {showEditDrawer && selectedCalendarEvent && (
          <CalendarEventEditDrawer
            toggle={() => setShowEditDrawer(false)}
            selectedEntity={selectedCalendarEvent}
          />
        )}
        {showAddDrawer && (
          <CalendarEventAddDrawer toggle={() => setShowAddDrawer(false)} />
        )}
      </div>
    </MobileWarningDialog>
  );
};
export default IAPCalendar;
